import React, { useState, useEffect } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';

const EASE = [0.21, 0.47, 0.32, 0.98];

// Smoothly counts a value when it changes
const AnimatedNumber = ({ value }) => {
  const spring = useSpring(Number(value) || 0, { stiffness: 120, damping: 20, mass: 0.6 });
  const display = useTransform(spring, (v) => Math.round(v).toLocaleString());
  useEffect(() => { spring.set(Number(value) || 0); }, [spring, value]);
  return <motion.span>{display}</motion.span>;
};

// Same volume tiers as /pricing
const getGrowthPrice = (accounts) => {
  let per;
  if (accounts <= 2) per = 25;
  else if (accounts <= 5) per = 24;
  else if (accounts <= 7) per = 23;
  else if (accounts <= 10) per = 22;
  else per = 21; // up to 15
  return { per, monthly: accounts * per };
};

const PricingTeaser = ({ darkMode }) => {
  const [growthAccounts, setGrowthAccounts] = useState(5);
  const growth = getGrowthPrice(growthAccounts);
  const fillPct = ((growthAccounts - 2) / 13) * 100;
  const accent = darkMode ? '#06b6d4' : '#3b82f6';
  const track = darkMode ? '#374151' : '#e5e7eb';

  const plans = [
    {
      name: 'Starter', popular: false, dynamic: false,
      price: '$27', unit: '/mo', blurb: 'For testing Instagram outreach.',
      features: ['1 Instagram account', 'Up to 1,000 DMs / month', 'A/B tested openers', 'Automated follow-ups'],
    },
    {
      name: 'Growth', popular: true, dynamic: true,
      blurb: 'Scale with multiple accounts.',
      features: [`${growthAccounts} Instagram accounts`, 'Volume discount as you scale', 'Everything in Starter', 'Unified inbox + analytics'],
    },
    {
      name: 'Scale', popular: false, dynamic: false,
      price: '$397', unit: '/mo', blurb: 'Unlimited outreach for agencies.',
      features: ['Unlimited accounts', 'Unlimited DMs', 'Priority support', 'Everything in Growth'],
    },
  ];

  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Simple, competitive pricing
          </h2>
          <p className={`mt-3 text-lg max-w-2xl mx-auto ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
            30–40% below competitors. Start with a 3-day free trial — no card required.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl mx-auto items-start">
          {plans.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
              whileHover={{ y: -10, transition: { type: 'spring', stiffness: 300, damping: 22 } }}
              className={`relative rounded-2xl p-7 transition-shadow duration-300 hover:shadow-2xl ${
                p.popular
                  ? (darkMode ? 'bg-slate-800 border-2 border-cyan-500 shadow-xl shadow-cyan-500/20' : 'bg-white border-2 border-blue-500 shadow-xl shadow-blue-500/20')
                  : (darkMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-gray-200 shadow-sm')
              }`}
            >
              {p.popular && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.6, y: -8 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.35, type: 'spring', stiffness: 420, damping: 16 }}
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold text-white ${darkMode ? 'bg-cyan-500' : 'bg-blue-500'} shadow-lg`}
                >
                  Most Popular
                </motion.div>
              )}
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{p.name}</h3>
              <p className={`text-sm mt-1 mb-4 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{p.blurb}</p>

              {p.dynamic ? (
                <div className="mb-5">
                  <div>
                    <span className={`text-4xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>$<AnimatedNumber value={growth.monthly} /></span>
                    <span className={`text-base ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>/mo</span>
                  </div>
                  <div className={`text-sm mt-1 mb-3 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {growthAccounts} accounts · ${growth.per}/account
                  </div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>Accounts</span>
                    <span className={`font-semibold ${darkMode ? 'text-cyan-300' : 'text-blue-600'}`}>{growthAccounts}</span>
                  </div>
                  <input
                    type="range" min="2" max="15" value={growthAccounts}
                    onChange={(e) => setGrowthAccounts(parseInt(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{ background: `linear-gradient(to right, ${accent} 0%, ${accent} ${fillPct}%, ${track} ${fillPct}%, ${track} 100%)` }}
                  />
                </div>
              ) : (
                <div className="mb-5">
                  <span className={`text-4xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{p.price}</span>
                  <span className={`text-base ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{p.unit}</span>
                </div>
              )}

              <ul className="space-y-2.5 mb-7 text-left">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${darkMode ? 'text-cyan-400' : 'text-blue-500'}`} />
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{f}</span>
                  </li>
                ))}
              </ul>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/signup?trial=true"
                  className={`w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full font-semibold transition-shadow ${
                    p.popular
                      ? `text-white ${darkMode ? 'bg-gradient-to-r from-cyan-500 to-blue-600' : 'bg-gradient-to-r from-blue-500 to-purple-600'} shadow-lg hover:shadow-xl`
                      : (darkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-gray-900 hover:bg-gray-200')
                  }`}
                >
                  Start free trial <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link to="/pricing" className={`text-sm font-medium ${darkMode ? 'text-slate-400 hover:text-cyan-300' : 'text-gray-500 hover:text-blue-600'}`}>
            Need the full breakdown? See all plans &amp; FAQ →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingTeaser;
